import {_} from 'lodash';
import * as ko from "knockout";
import ObjectDetails from "./ObjectDetails";
import PromiseRequest from "../../req/PromiseRequest";
import {ErrorTracker} from "../../error.tracker";
import {IAnchor, ICompanySyncstep, IKnockoutComponent, IRouteDefinitionParams, ISyncstep} from "../../index";
import {createErrorMessage, getObjectDefinition, IObjectDefinition, validateObjectGuid} from "../../util/misc";
import {global} from "../../appContext";
import {nsFa} from "../fa/fn";
import {PermissionValidator} from "../../auth/PermissionValidator";
import {nsSync} from "../../util/nsSync";
import {UPDATING_MESSAGE} from "../../constants/Language";
import {SyncstepId} from "../../constants";
import i = nsFa.i;
import isSyncstepId = nsSync.isSyncstepId;
import isSyncstepActiveDirectory = nsSync.isSyncstepActiveDirectory;
import anchorHtml from './anchor.html?raw';

export let template: string = anchorHtml;

interface IExportStep {
    name: string
    syncstep: ISyncstep
}

const syncstepRequiresGuid = (syncstepId: ISyncstep['id']): boolean => isSyncstepId(syncstepId, SyncstepId.Export_AD, SyncstepId.Export_AD2, SyncstepId.Office365)

export class viewModel extends ObjectDetails {
    private readonly pv: PermissionValidator = global.permissionValidator;
    objDef: IObjectDefinition;
    anchors: KnockoutObservableArray<AnchorMutable> = ko.observableArray<AnchorMutable>([]);
    errorTracker: ErrorTracker = global.errorAreas.get("anchor");

    private readonly req_emptycache: PromiseRequest = global.getPromiseRequest("emptycache");
    private readonly req_fullobjectexport: PromiseRequest = global.getPromiseRequest('fullobjectexport');
    private readonly req_getcompanysyncsteps: PromiseRequest = global.getPromiseRequest('getcompanysyncsteps');
    private readonly req_getsyncsteps: PromiseRequest = global.getPromiseRequest('getsyncsteps');
    private syncsteps!: ISyncstep[];

    constructor(params: IRouteDefinitionParams) {
        super(params, false);
        this.objDef = getObjectDefinition(this.__objectTypeId!)!;
        this.request();
    }

    protected override request(): Promise<any> {
        return super.request()
            .then(() => {
                if (this.pv.isMinSuperManager()) {
                    return this.req_getcompanysyncsteps
                        .request()
                        .then((data: ICompanySyncstep[]) => this.syncsteps = data.map(o => o.syncstep))
                } else {
                    return this.req_getsyncsteps
                        .request()
                        .then((data: ISyncstep[]) => this.syncsteps = data)
                }
            })
            .then(() => {
                const objectData = this.data();
                if (!objectData) {
                    return;
                }
                const anchors: AnchorMutable[] = objectData.anchor.map(obj => new AnchorMutable(obj));
                this.anchors(anchors);
                this.messageData(null);
                this.isReady(true);
            })
            .catch(super.onError.bind(this))
    }

    onDeleteClick(obj: AnchorMutable) {
        this.anchors.remove(obj);
    }

    onAddClick() {
        const obj = new AnchorMutable();
        this.anchors.push(obj);
    }

    onRemoveClick(obj: AnchorMutable) {
        this.anchors.remove(obj);
    }

    isFormValid = ko.pureComputed(() => this.validateForm());

    validateForm(): boolean {
        let isValid = true;
        this.anchors().forEach((obj: AnchorMutable) => {
            const a = obj.isValid();
            this.errorTracker.update(!a, obj.anchor, "((invalid)) ((anchor|ac))");
            const b = obj.syncstep() === null || !!obj.syncstep();
            this.errorTracker.update(!b, obj.syncstep, "((invalid)) ((synchronization template|ac))");
            isValid = isValid && a && b;
        });
        return isValid;
    }

    isFormChanged = ko.pureComputed((): boolean => {
        const a = !!this.getAdded().length;
        const b = !!this.getUpdated().length;
        const c = !!this.getDeleted().length;
        return a || b || c;
    });

    getAdded(): IAnchor[] {
        return this.anchors()
            .filter(obj => obj.isAdded())
            .map(obj => this.mutableToStatic(obj));
    }

    getUpdated(): IAnchor[] {
        return this.anchors()
            .filter(obj => obj.isChanged())
            .map(obj => this.mutableToStatic(obj));
    }

    getDeleted(): IAnchor[] {
        const objectData = this.data();
        if (!objectData) {
            return [];
        }
        const anchors = this.anchors().map(obj => obj.$original);
        return objectData.anchor.filter(obj => !anchors.find((b: IAnchor) => obj.id == b.id));
    }

    mutableToStatic(obj: AnchorMutable): IAnchor {
        const objectData = this.data();
        return {
            anchor: obj.anchor(),
            deleted: obj.$original.deleted,
            exporttemplateid: obj.$original.exporttemplateid,
            id: obj.$original.id,
            immutableid: obj.$original.immutableid,
            sourceid: obj.syncstep()?.id ?? 0,
            userid: objectData?.id ?? 0
        }
    }

    onSubmit() {
        this.isReady(false);
        this.messageData({message: UPDATING_MESSAGE});
        Promise.resolve()
            .then(() => {
                // delete
                return Promise.all(this.getDeleted().map((item: IAnchor) => global.getPromiseRequest("deleteobjectanchor").request({id: item.id})))
            })
            .then(() => {
                // update
                return Promise.all(this.getUpdated().map((item: IAnchor) => global.getPromiseRequest("updateobjectanchor").request({janchor: JSON.stringify(item)})))
            })
            .then(() => {
                // add
                return Promise.all(this.getAdded().map((item: IAnchor) => global.getPromiseRequest("createobjectanchor").request({janchor: JSON.stringify(item)})))
            })
            .then(() => this.req_emptycache.request())
            .then(() => {
                global.cacheManager.removeCache("getobject");
                global.snackbarData({message: `updated anchor`});
                this.request();
            })
            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))))
        ;
    }

    onDiscardChangesClick() {
        const objectData = this.data();
        if (!objectData) {
            return;
        }
        const anchors: AnchorMutable[] = objectData.anchor.map(obj => new AnchorMutable(obj));
        this.anchors(anchors);
        this.messageData(null);
        this.isReady(true);
    }

    private getExportSyncsteps(): IExportStep[] {
        return _(this.syncsteps)
            .filter((s: ISyncstep) => {
                const a = s.isexportable;
                const b = isSyncstepActiveDirectory(s.id) || this.pv.isMinSuperManager();
                return a && b
            })
            .map((s: ISyncstep) => {
                const regExp = /Export\s*(.*)/i;
                const ret: IExportStep = {
                    name: regExp.test(s.name) ? (regExp.exec(s.name)?.[1] ?? s.name) : s.name,
                    syncstep: s,
                };
                return ret;
            })
            .sortBy((e: IExportStep) => e.name.toLowerCase())
            .value()
    }

    private onRecreateClick() {
        const dialog = {
            title:  `${i('fa-recycle')} ((restore))`,
            closeable: ko.observable(true),
        };
        const searchObjectParams = _.defaults({
            pagenr: 0,
            onlyids: true
        });
        const dialogOptions = {
            dialog: dialog,
            component: {
                name: 'component/object/dialog/recreate',
                params: {
                    data: [this.data()],
                    dialog: dialog,
                    numTotal: 1,
                    objecttypeid: this.__objectTypeId,
                    searchObjectParams: searchObjectParams,
                }
            }
        };
        global.dialogComponent(dialogOptions);
    }

    private getInvalidGuidAnchorMessage(): string {
        const index = this.__routeDef.objectTypeId ?? 0;
        return [
            'message: invalid guid anchor', // fallback
            'message: invalid user guid anchor', // User
            'message: invalid department guid anchor', // Department
            'message: invalid group guid anchor', // Group
        ][index] ?? 'message: invalid guid anchor';
    }

    override dispose() {
        super.dispose();
        this.req_getsyncsteps.abort()
        this.req_getcompanysyncsteps.abort()
        this.req_fullobjectexport.abort()
    }

}

class AnchorMutable {
    readonly $original: IAnchor;
    readonly $isEditing: KnockoutObservable<boolean>;
    readonly anchor: KnockoutObservable<string>;
    readonly syncstep: KnockoutObservable<ISyncstep | null>;

    constructor(obj: IAnchor | null = null) {
        if (obj == null) {
            this.$original = {
                anchor: "",
                deleted: false,
                exporttemplateid: 0,
                id: 0,
                immutableid: "",
                sourceid: 0,
                userid: 0,
            };
            this.$isEditing = ko.observable(true);
            this.anchor = ko.observable("");
            this.syncstep = ko.observable(null);

        } else {
            this.$original = obj;
            this.$isEditing = ko.observable(false);
            this.anchor = ko.observable(obj.anchor);
            this.syncstep = ko.observable(null);
        }
    }

    isValid(): boolean {
        const str = this.anchor();
        if (!str) {
            return false;
        }

        if (this.requiresGuid()) {
            return validateObjectGuid(str);
        }

        return true;
    }

    private requiresGuid(): boolean {
        return syncstepRequiresGuid(this.$original.sourceid)
    }

    private validateObjectGuid(): boolean {
        const str = this.anchor() ?? '';
        return validateObjectGuid(str);
    }

    isChanged(): boolean {
        if (!this.$isEditing()) {
            return false;
        }
        const o = this.$original;
        const a = !!o.id;
        const b = this.anchor() != o.anchor;
        const c = this.syncstep()?.id != o.sourceid;
        return a && (b || c);
    }

    isAdded(): boolean {
        return !this.$original.id;
    }

    private getSyncstepComponent(): IKnockoutComponent {
        return {
            name: 'component/variable/syncstep-or-companysyncstep',
            params: {id: this.$original.sourceid, showIcon: true, linkable: global.permissionValidator.superAdmin()},
        }
    }


    onAbortClick() {
        this.anchor(this.$original.anchor);
        this.$isEditing(false);
    }

}